import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import {
  BAR_CHART_SCHEME,
  CHART_CONFIG,
  LINE_CHART_SCHEME
} from '../../../../shared/constants';
import { formatChartBucketLabel } from '@app/shared/utilities/chart-bucket-label';

export type ChartType = 'line' | 'bar-grouped' | 'bar-stacked';
export type ChartGrain = 'day' | 'week' | 'month';

interface ChartLegendItem {
  key: string;
  name: string;
  color: string;
  enabled: boolean;
}

@Component({
  selector: 'app-analytics-chart',
  templateUrl: './analytics-chart.component.html',
  standalone: false,
})
export class AnalyticsChartComponent implements OnInit, OnDestroy, OnChanges {
  @Input() chartType: ChartType = 'line';
  @Input() data: any[] = [];
  @Input() height = 300;
  @Input() grain?: ChartGrain;
  @Input() scheme?: any;
  @Input() legend = false;
  @Input() toggleableLegend = false;

  translatedData: any[] = [];
  displayedData: any[] = [];
  displayedScheme: any;
  legendItems: ChartLegendItem[] = [];
  private axisLabelByName = new Map<string, string>();
  private hiddenSeriesKeys = new Set<string>();
  private destroy$ = new Subject<void>();

  chartConfig = CHART_CONFIG;
  lineChartScheme = LINE_CHART_SCHEME;
  barChartScheme = BAR_CHART_SCHEME;

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.translateData());
  }

  ngOnChanges(changes: SimpleChanges): void {
    // New datasets start with every series visible, regardless of the loading-state template.
    if (changes['data']) {
      this.hiddenSeriesKeys.clear();
    }
    if (changes['data'] || changes['grain']) {
      this.translateData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  xAxisTickFormatting = (value: string): string =>
    this.axisLabelByName.get(value) ?? value;

  toggleSeries(key: string): void {
    if (this.hiddenSeriesKeys.has(key)) {
      this.hiddenSeriesKeys.delete(key);
    } else {
      this.hiddenSeriesKeys.add(key);
    }
    this.updateDisplayedSeries();
  }

  private translateData(): void {
    this.axisLabelByName.clear();
    this.translatedData = this.data.map((item: any) => {
      let name: string;
      // Volume buckets use localized dates; legacy charts use App.Charts translation keys.
      if (this.grain) {
        const label = formatChartBucketLabel(
          item.start,
          item.end,
          this.grain,
          this.translate.currentLang
        );
        name = label.tooltip;
        // Keep the detailed bucket name for tooltips while shortening the x-axis label.
        if (label.tooltip !== label.axis) {
          this.axisLabelByName.set(label.tooltip, label.axis);
        }
      } else {
        name = this.translate.instant(`App.Charts.${item.name}`);
      }

      if (Array.isArray(item.series)) {
        return {
          name,
          series: item.series.map((series: any) => ({
            key: series.key ?? series.name,
            name: this.translate.instant(
              `App.Charts.${series.key ?? series.name}`
            ),
            value: series.value,
          })),
        };
      }
      return { name, value: item.value };
    });
    this.legendItems = this.buildLegend();
    const availableKeys = new Set(this.legendItems.map(item => item.key));
    this.hiddenSeriesKeys.forEach(key => {
      if (!availableKeys.has(key)) this.hiddenSeriesKeys.delete(key);
    });
    this.updateDisplayedSeries();
  }

  private buildLegend(): ChartLegendItem[] {
    const series = this.translatedData[0]?.series;
    if (!series) return [];
    const domain = (this.scheme || this.barChartScheme)?.domain ?? [];
    return series.map((item: any, index: number) => ({
      key: item.key,
      name: item.name,
      color: domain[index % domain.length],
      enabled: !this.hiddenSeriesKeys.has(item.key)
    }));
  }

  // Rebuild the legend state, visible chart series and matching colour scheme for the current selection.
  private updateDisplayedSeries(): void {
    this.legendItems = this.legendItems.map(item => ({
      ...item,
      enabled: !this.hiddenSeriesKeys.has(item.key)
    }));
    this.displayedData = this.translatedData.map(item => {
      if (!Array.isArray(item.series)) return item;
      return {
        ...item,
        series: item.series.filter(
          (series: any) => !this.hiddenSeriesKeys.has(series.key)
        )
      };
    });

    const baseScheme = this.scheme || this.barChartScheme;
    this.displayedScheme = {
      ...baseScheme,
      domain: this.legendItems
        .filter(item => item.enabled)
        .map(item => item.color)
    };
  }
}
